/**
 * Bye For Now dashboard — every number that matters, on one page, behind a
 * password.
 *
 * The password is checked server-side by `api/stats.ts`; nothing here decides
 * access, and no data exists in the bundle until that call comes back. It is
 * held in sessionStorage so a refresh doesn't re-prompt, and dies with the tab.
 *
 * The page is ordered by what it would cost to be wrong about, not by what is
 * easiest to measure. Conversations first, because that is the product
 * happening. Then the funnel, because it says which half of the bet is losing.
 * Then retention and reachability, which are the two ways it loses. Delivery
 * and version health are last but are not filler: an install base that cannot
 * be rung is a silent outage, and it has happened here before.
 */

import React, { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { BarList, CohortGrid, Columns, DualSeries, Funnel,
         type BarRow, type CohortRow } from '../components/stats/Charts'
import { daysBefore, fillDays, fmt, shortDay, type Point } from '../components/stats/format'
import './stats.css'

const STORAGE_KEY = 'byefornow.stats.key'

// ---------------------------------------------------------------------------
// Payload shape — mirrors CORE_SQL and LIVE_SQL in api/stats.ts
// ---------------------------------------------------------------------------

type Num = number | null

type Payload = {
  generated_at: string
  population: {
    user_rows: number
    real_users: number
    active_7d: number
    active_30d: number
    talked_30d: number
    dormant: number
    friendless: number
    conversations_30d: number
    conversations_7d: number
    capture_started_at: string
  }
  retention_headline: { window_days: number; eligible: number; opened: number; talked: number }[]
  retention_curve: {
    cohort_week: string; cohort_size: number; week_offset: number
    opened: number; talked: number; complete: boolean
  }[]
  engagement: { day: string; dau: number; wau: number; mau: number; wau_talked: number }[]
  lifecycle: { day: string; new_users: number; retained: number; resurrected: number; came_back: number }[]
  tenure: { bucket: string; sort: number; users: number }[]
  conversations: {
    day: string; conversations: number; real_conversations: number
    talk_seconds: number; median_seconds: number; largest: number; people_who_talked: number
  }[]
  conversation_totals: { total: number; talk_seconds: number; median_seconds: number; group_calls: number }
  call_funnel: {
    day: string; presses: number; reached_someone: number; reached_nobody: number
    rang: number; answered: number; talked_30s: number; direct: number
    callers: number; measurable: number
  }[]
  answer_by_mode: {
    mode: string; attempts: number; rang: number; answered: number
    talked_30s: number; median_seconds_to_answer: Num
  }[]
  answerers: { name: string; times_rung: number; times_answered: number }[]
  activation: {
    verified_a_phone: number; set_a_name: number; synced_contacts: number
    made_a_friend: number; pressed_once: number; ever_talked: number
    median_days_to_first_press: Num; press_window_users: number
  }
  signups: { day: string; rows_created: number; named: number; shell: number; with_push_token: number }[]
  graph: {
    users: number; edge_ends: number; friendships: number; edges_to_excluded: number
    mean_friends: number; median_friends: number; with_no_friends: number
    never_talked_to_anyone: number; nobody_free_right_now: number
  }
  friend_distribution: { bucket: string; sort: number; users: number }[]
  notes: { day: string; notes: number; senders: number; read: number }[]

  // LIVE_SQL — absent when live_error is set
  supply?: {
    users: number; present_now: number; reachable_by_name_now: number
    blocked_quiet_hours: number; blocked_window: number; blocked_paused: number
    blocked_ended_day: number; in_a_room: number
  }
  reachable_distribution?: { bucket: string; sort: number; users: number }[]
  hour_of_day?: { hour: number; presses: number; conversations: number; windows_open: number }[]
  delivery?: {
    users: number; missing_fcm: number; ios_missing_voip: number
    ios_missing_live_activity: number; unknown_platform: number
  }
  versions?: { version: string; platform: string; users: number; active_30d: number; ringable: boolean }[]
  push_volume?: {
    day: string; sent: number; rings: number; asks: number
    surfaces: number; other: number; active_users: number
  }[]
  push_by_type?: { type: string; sent: number; last_sent: string }[]
  live_error?: string | null
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function Stat({ label, value, sub, tone }: {
  label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'good' | 'warn' | 'bad'
}) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <strong className={`stat-value${tone ? ` tone-${tone}` : ''}`}>{value}</strong>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  )
}

function Section({ id, title, blurb, children }: {
  id: string; title: string; blurb?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <section className="section" id={id}>
      <div className="section-head">
        <h2>{title}</h2>
        {blurb && <p>{blurb}</p>}
      </div>
      {children}
    </section>
  )
}

function Card({ title, hint, children, wide }: {
  title: string; hint?: React.ReactNode; children: React.ReactNode; wide?: boolean
}) {
  return (
    <div className={`card${wide ? ' card-wide' : ''}`}>
      <h3>{title}</h3>
      {hint && <p className="card-hint">{hint}</p>}
      {children}
    </div>
  )
}

/** Seconds → "4m 12s" / "48s". Durations on this page are minutes, not hours. */
function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return s ? `${m}m ${s}s` : `${m}m`
}

const pct = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '—')

// ---------------------------------------------------------------------------

export default function StatsPage() {
  const [password, setPassword] = useState('')
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async (key: string) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: key }),
      })
      const body = await res.json()
      if (!res.ok) {
        setError(body.error ?? 'Something went wrong')
        sessionStorage.removeItem(STORAGE_KEY)
        setData(null)
        return
      }
      sessionStorage.setItem(STORAGE_KEY, key)
      setData(body as Payload)
    } catch {
      setError('Could not reach the server.')
    } finally {
      setLoading(false)
    }
  }, [])

  // A key already in sessionStorage means this tab authenticated earlier; the
  // server still re-checks it on every load, so this only skips the prompt.
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY)
    if (saved) load(saved)
  }, [load])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (password.trim()) load(password.trim())
  }

  if (!data) {
    return (
      <div className="gate">
        <form className="gate-card" onSubmit={onSubmit}>
          <h1>Bye For Now</h1>
          <p>Private dashboard. Enter the password to continue.</p>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            autoFocus
            autoComplete="current-password"
            aria-label="Dashboard password"
          />
          <button type="submit" disabled={loading || !password.trim()}>
            {loading ? 'Checking…' : 'View stats'}
          </button>
          {error && <p className="gate-error">{error}</p>}
        </form>
      </div>
    )
  }

  return <Dashboard data={data} onReload={() => load(sessionStorage.getItem(STORAGE_KEY) ?? '')} loading={loading} />
}

// ---------------------------------------------------------------------------

function Dashboard({ data, onReload, loading }: {
  data: Payload; onReload: () => void; loading: boolean
}) {
  const p = data.population
  const today = data.generated_at.slice(0, 10)

  // The day the capture triggers went in. Before it, nothing recorded who
  // joined a room, so every conversation-derived number is genuinely absent
  // rather than zero — and the page says which is which instead of drawing a
  // flat line through months in which people demonstrably talked.
  const captureDay = p.capture_started_at.slice(0, 10)
  const measuring = useMemo(
    () => Math.max(0, Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${captureDay}T00:00:00Z`)) / 86_400_000)),
    [today, captureDay],
  )
  const young = measuring < 14

  const captureNote = (
    <>
      Conversation and answer data starts <strong>{shortDay(captureDay)}</strong>
      {measuring === 0 ? ' — today' : `, ${measuring} day${measuring === 1 ? '' : 's'} ago`}, when the
      capture triggers went in. Nothing before that recorded who joined a room, so those months are
      blank rather than zero.
    </>
  )

  // ── Series ────────────────────────────────────────────────────────────────
  const convo90 = useMemo<Point[]>(
    () => fillDays(data.conversations, 90, (r, day) => ({
      label: shortDay(day),
      value: r?.conversations ?? 0,
      note: r ? `${r.people_who_talked} people · median ${duration(r.median_seconds)}` : undefined,
    })),
    [data.conversations],
  )

  const press90 = useMemo<Point[]>(
    () => fillDays(data.call_funnel, 90, (r, day) => ({
      label: shortDay(day),
      value: r?.presses ?? 0,
      note: r ? `${r.callers} caller${r.callers === 1 ? '' : 's'} · ${r.reached_nobody} reached nobody` : undefined,
    })),
    [data.call_funnel],
  )

  const active90 = useMemo(
    () => fillDays(data.engagement, 90, (r, day) => ({
      label: shortDay(day), value: r?.dau ?? 0,
      note: r ? `${r.wau} weekly · ${r.mau} monthly` : undefined,
    })).map((pt, i) => ({
      label: pt.label,
      a: pt.value,
      b: fillDays(data.engagement, 90, r => ({ label: '', value: r?.wau ?? 0 }))[i].value,
      note: pt.note,
    })),
    [data.engagement],
  )

  const signup365 = useMemo<Point[]>(
    () => fillDays(data.signups, 120, (r, day) => ({
      label: shortDay(day),
      value: r?.rows_created ?? 0,
      note: r ? `${r.named} set a name, ${r.shell} never did` : undefined,
    })),
    [data.signups],
  )

  // ── Retention ─────────────────────────────────────────────────────────────
  const headline = useMemo(() => {
    const by = new Map(data.retention_headline.map(h => [h.window_days, h]))
    return [1, 7, 30, 90].map(d => ({ days: d, ...(by.get(d) ?? { eligible: 0, opened: 0, talked: 0 }) }))
  }, [data.retention_headline])

  const cohorts = useMemo<CohortRow[]>(() => {
    const byWeek = new Map<string, { size: number; cells: (number | null)[] }>()
    for (const r of data.retention_curve) {
      if (!byWeek.has(r.cohort_week)) {
        byWeek.set(r.cohort_week, { size: r.cohort_size, cells: [] })
      }
      byWeek.get(r.cohort_week)!.cells[r.week_offset] = (r.opened / r.cohort_size) * 100
    }
    const now = Date.parse(`${today}T00:00:00Z`)
    return Array.from(byWeek.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 14)
      .map(([week, v]) => ({
        label: shortDay(week),
        size: v.size,
        cells: Array.from({ length: 9 }, (_, i) => {
          if (v.cells[i] !== undefined) return v.cells[i]
          // `retention_curve` only emits rows for weeks somebody was active, so
          // a missing cell is one of two completely different things. If the
          // whole cohort has lived through that week, nobody came back and the
          // answer is 0. If it hasn't happened yet the answer is unknown, and
          // the grid hatches it — filling those with 0 would draw a retention
          // cliff that is really just the calendar.
          const weekEnds = Date.parse(`${week}T00:00:00Z`) + (i + 1) * 7 * 86_400_000
          return now >= weekEnds ? 0 : null
        }),
      }))
  }, [data.retention_curve, today])

  // ── Funnels ───────────────────────────────────────────────────────────────
  const activation = data.activation
  // Only the steps that genuinely NEST go in the funnel. Contact sync doesn't:
  // you can be added by a friend without ever granting the permission, and old
  // clients never wrote the rows at all — drawn as a funnel step it produced a
  // stage with MORE people after it than in it, and a drop label for a drop
  // that never happened. Same for the two censored measures below, which are
  // observed over shorter windows than the steps above them.
  const activationSteps = [
    { label: 'Verified a phone number', value: activation.verified_a_phone, note: 'A row exists. Not yet a person.' },
    { label: 'Set a name', value: activation.set_a_name, note: 'The first act that needs a human.' },
    { label: 'Has at least one friend', value: activation.made_a_friend, note: 'Below this line the app cannot work at all — no amount of retention work reaches these people.' },
  ]

  const window30 = useMemo(() => {
    const since = daysBefore(today, 30)
    const rows = data.call_funnel.filter(r => r.day > since)
    const sum = (k: keyof (typeof rows)[number]) => rows.reduce((a, r) => a + (r[k] as number), 0)
    return {
      presses: sum('presses'), reached: sum('reached_someone'), nobody: sum('reached_nobody'),
      rang: sum('rang'), answered: sum('answered'), talked: sum('talked_30s'),
      measurable: sum('measurable'),
    }
  }, [data.call_funnel, today])

  const supply = data.supply
  const blockedRows: BarRow[] = supply
    ? [
        { label: 'Availability window not open yet', value: supply.blocked_window,
          meta: 'the daily "I\'m home" window' },
        { label: 'In quiet hours', value: supply.blocked_quiet_hours, meta: 'their own setting' },
        { label: 'Ended the day', value: supply.blocked_ended_day, meta: 'tapped Done' },
        { label: 'Paused', value: supply.blocked_paused, meta: 'do not disturb' },
        { label: 'Already in a call', value: supply.in_a_room, meta: 'busy, which is good' },
      ].filter(r => r.value > 0)
    : []

  return (
    <div className="dash">
      <header className="dash-head">
        <div>
          <h1>Bye For Now</h1>
          <p>
            Generated {new Date(data.generated_at).toLocaleString('en-US', {
              dateStyle: 'medium', timeStyle: 'short',
            })} · all dates UTC
          </p>
        </div>
        <button className="refresh" onClick={onReload} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {data.live_error && (
        <div className="banner banner-bad">
          Live reachability and delivery sections are unavailable. {data.live_error}
        </div>
      )}

      {young && (
        <div className="banner">{captureNote}</div>
      )}

      {/* ═══ Headline ═══════════════════════════════════════════════════════ */}
      <Section
        id="headline"
        title="Where things stand"
        blurb={
          <>
            The only number that says the product worked is a conversation: two people in the
            same room at the same time. Everything else on this page is either someone trying
            to get there, or a reason they couldn't.
          </>
        }
      >
        <div className="stat-row">
          <Stat label="Conversations, 7 days" value={fmt(p.conversations_7d)}
                sub={`${fmt(p.conversations_30d)} in 30 days`} />
          <Stat label="Talked in 30 days" value={fmt(p.talked_30d)}
                sub={`of ${fmt(p.real_users)} real users`}
                tone={p.talked_30d === 0 ? 'bad' : p.talked_30d < p.active_30d / 2 ? 'warn' : 'good'} />
          <Stat label="Active, 7 days" value={fmt(p.active_7d)} sub={`${fmt(p.active_30d)} in 30 days`} />
          <Stat label="Real users" value={fmt(p.real_users)}
                sub={`${fmt(p.user_rows)} rows, ${fmt(p.user_rows - p.real_users)} never named`} />
          <Stat label="Dormant" value={fmt(p.dormant)} sub="30+ days silent"
                tone={p.dormant > p.real_users / 2 ? 'warn' : undefined} />
          <Stat label="Nobody to call" value={fmt(p.friendless)} sub="users with zero friends"
                tone={p.friendless > 0 ? 'warn' : 'good'} />
        </div>
      </Section>

      {/* ═══ Conversations ══════════════════════════════════════════════════ */}
      <Section
        id="conversations"
        title="Conversations"
        blurb={captureNote}
      >
        <div className="grid">
          <Card title="Conversations a day" hint="Two or more people overlapping in a room, swept from join and leave times." wide>
            <Columns points={convo90} emptyLabel={`Nothing recorded yet — capture started ${shortDay(captureDay)}.`} />
          </Card>
          <Card title="Totals since capture began">
            <div className="stat-col">
              <Stat label="Conversations" value={fmt(data.conversation_totals.total)} />
              <Stat label="Median length" value={duration(data.conversation_totals.median_seconds)} />
              <Stat label="Total talk time" value={duration(data.conversation_totals.talk_seconds)} />
              <Stat label="Group calls" value={fmt(data.conversation_totals.group_calls)} sub="three or more people" />
            </div>
          </Card>
        </div>
      </Section>

      {/* ═══ The call funnel ════════════════════════════════════════════════ */}
      <Section
        id="funnel"
        title="Press to conversation"
        blurb={
          <>
            The diagnostic. A press that <em>reaches nobody</em> is a supply failure — there was
            no one to call. A ring that <em>isn't answered</em> is a demand failure — someone
            was there and didn't pick up. They need opposite fixes, so they are never added together.
          </>
        }
      >
        <div className="grid">
          <Card title="Last 30 days">
            <Funnel
              steps={[
                { label: 'Pressed', value: window30.presses, note: 'Opened a lobby or called by name' },
                { label: 'Reached somebody', value: window30.reached, note: `${window30.nobody} found nobody at all` },
                { label: 'Rang a phone', value: window30.rang, note: 'The rest arrived as a quiet surface' },
                {
                  label: 'Answered',
                  value: window30.answered,
                  note: window30.measurable === 0
                    ? `Not recorded for any of these — capture began ${shortDay(captureDay)}`
                    : `${window30.measurable} of these ${window30.presses} are measurable`,
                  // Suppress the step-over-step drop where nothing was measured.
                  // "100% dropped here" about a stage with no instrument is a
                  // fabricated finding, and it is the loudest thing on the card.
                  unmeasured: window30.measurable === 0,
                },
                { label: 'Talked 30s or more', value: window30.talked, unmeasured: window30.measurable === 0 },
              ]}
            />
          </Card>
          <Card title="Presses a day" hint="Every attempt to start a conversation, however it ended." wide>
            <Columns points={press90} />
          </Card>
        </div>

        <div className="grid">
          <Card title="Does being named help?" hint="Whether a call you chose gets picked up more often than one the app chose for you." wide>
            {data.answer_by_mode.length ? (
              <table className="table">
                <thead>
                  <tr><th>Mode</th><th>Attempts</th><th>Rang</th><th>Answered</th><th>Rate</th><th>Median wait</th></tr>
                </thead>
                <tbody>
                  {data.answer_by_mode.map(m => (
                    <tr key={m.mode}>
                      <th scope="row">{m.mode}</th>
                      <td>{fmt(m.attempts)}</td>
                      <td>{fmt(m.rang)}</td>
                      <td>{fmt(m.answered)}</td>
                      <td>{pct(m.answered, m.attempts)}</td>
                      <td>{m.median_seconds_to_answer === null ? '—' : duration(m.median_seconds_to_answer)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="viz-empty">
                No answerable calls since {shortDay(captureDay)}. This fills in as soon as one ring is answered.
              </div>
            )}
          </Card>
          <Card title="Who picks up" hint="Rung versus answered, per person. A rate built from one reliable friend and nine who never answer is a different product from one where everyone answers sometimes.">
            {data.answerers.length ? (
              <BarList
                rows={data.answerers.map(a => ({
                  label: a.name,
                  value: Math.round((a.times_answered / a.times_rung) * 100),
                  weight: a.times_rung,
                  meta: `${a.times_answered}/${a.times_rung} rung`,
                  tone: a.times_answered === 0 ? 'bad' : undefined,
                }))}
                unit="%"
              />
            ) : (
              <div className="viz-empty">Nobody has been rung since capture began.</div>
            )}
          </Card>
        </div>
      </Section>

      {/* ═══ Retention ══════════════════════════════════════════════════════ */}
      <Section
        id="retention"
        title="Retention"
        blurb={
          <>
            Reported twice, on two definitions of active. <strong>Opened</strong> is the app
            being launched; <strong>talked</strong> is a conversation actually happening. The gap
            between them is the whole story — someone who opens every day and never talks is not
            a retained user, they are a user about to leave.
            <br />
            These four are <em>cumulative</em> — the share who came back at any point inside the
            window, so they rise as the window widens. Classic day-N retention on a base of{' '}
            {fmt(p.real_users)} would be one person's Tuesday. The denominator shrinks with the
            window because only people who signed up long enough ago can be counted at all.
          </>
        }
      >
        <div className="stat-row">
          {headline.map(h => (
            <Stat
              key={h.days}
              label={h.days === 1 ? 'Within a day' : h.days === 7 ? 'Within a week' : `Within ${h.days} days`}
              value={pct(h.opened, h.eligible)}
              sub={
                <>
                  {h.opened} of {h.eligible} came back
                  <br />
                  <span className="stat-second">{h.talked} of them talked</span>
                </>
              }
            />
          ))}
        </div>

        <div className="grid">
          <Card
            title="By signup cohort"
            hint="Share of each week's signups still opening the app, by week since. Blank cells are weeks that haven't happened yet, not weeks with nobody in them."
            wide
          >
            <CohortGrid rows={cohorts} columns={9} />
          </Card>
          <Card title="How long people stick around" hint="Total days each person has ever been active.">
            <BarList
              rows={data.tenure
                .sort((a, b) => a.sort - b.sort)
                .map(t => ({
                  label: t.bucket,
                  value: t.users,
                  meta: pct(t.users, p.real_users),
                  tone: t.sort <= 1 ? 'bad' : t.sort >= 4 ? 'good' : undefined,
                }))}
              unit=" users"
              unitOne=" user"
            />
          </Card>
        </div>
      </Section>

      {/* ═══ Engagement ═════════════════════════════════════════════════════ */}
      <Section
        id="engagement"
        title="Who is here"
        blurb="Daily against weekly actives, and where each day's actives came from."
      >
        <div className="grid">
          <Card title="Active users" wide>
            <DualSeries points={active90} names={['Daily', 'Weekly']} />
          </Card>
          <Card title="Stickiness" hint="Daily actives as a share of monthly. Above 20% is a habit; below 10% is an app people remember rather than use.">
            <div className="stat-col">
              {(() => {
                const last = data.engagement[data.engagement.length - 1]
                if (!last) return <div className="viz-empty">No data yet</div>
                return (
                  <>
                    <Stat label="DAU / MAU" value={pct(last.dau, last.mau)}
                          tone={last.mau === 0 ? undefined : last.dau / last.mau >= 0.2 ? 'good' : last.dau / last.mau >= 0.1 ? 'warn' : 'bad'} />
                    <Stat label="Daily" value={fmt(last.dau)} />
                    <Stat label="Weekly" value={fmt(last.wau)} sub={`${last.wau_talked} of them talked`} />
                    <Stat label="Monthly" value={fmt(last.mau)} />
                  </>
                )
              })()}
            </div>
          </Card>
        </div>

        <div className="grid">
          <Card title="Where today's actives came from" hint="New on their first day, retained if they were here within a week, resurrected after a fortnight away." wide>
            <Columns
              points={fillDays(data.lifecycle, 60, (r, day) => ({
                label: shortDay(day),
                value: (r?.retained ?? 0) + (r?.resurrected ?? 0) + (r?.new_users ?? 0) + (r?.came_back ?? 0),
                note: r ? `${r.new_users} new · ${r.retained} retained · ${r.resurrected} resurrected` : undefined,
              }))}
            />
          </Card>
        </div>
      </Section>

      {/* ═══ Activation ═════════════════════════════════════════════════════ */}
      <Section
        id="activation"
        title="Getting started"
        blurb={
          <>
            The funnel does not end at signup, because this app is worth nothing to someone with
            no friends in it. The steps are ordered by what they cost the user: a name is free,
            and a friend needs somebody else to act. Wherever the cliff is, that is the real
            onboarding problem — and right now it is not the part anyone writes onboarding for.
          </>
        }
      >
        <div className="grid">
          <Card title="Signup to a usable app" hint="Only the steps that nest inside each other, so a drop is always a real drop." wide>
            <Funnel steps={activationSteps} />
          </Card>
          <Card
            title="Measured over shorter windows"
            hint="These three don't belong in the funnel above: each is observed over a different span, so they are not comparable to it or to each other."
          >
            <div className="stat-col">
              <Stat label="Synced contacts" value={fmt(activation.synced_contacts)}
                    sub="Not a prerequisite — a friend can add you. Older clients never wrote these rows." />
              <Stat label="Pressed the button" value={fmt(activation.pressed_once)}
                    sub={`Of the ${activation.press_window_users} who signed up since call history begins.`} />
              <Stat label="Ever really talked" value={fmt(activation.ever_talked)}
                    sub={`Of ${activation.set_a_name}, from the friendship timestamp — the one measure that reaches back before capture.`} />
            </div>
          </Card>
        </div>
        <div className="grid">
          <Card title="Signups a day" hint="Rows created against rows that became a person. A spike of unnamed rows is an SMS-pumping run, not growth." wide>
            <Columns points={signup365} />
          </Card>
        </div>
      </Section>

      {/* ═══ Friend graph ═══════════════════════════════════════════════════ */}
      <Section
        id="graph"
        title="The friend graph"
        blurb="The hard ceiling on everything else. No amount of retention work reaches a user with nobody to call."
      >
        <div className="stat-row">
          <Stat label="Friendships" value={fmt(data.graph.friendships)}
                sub={
                  <>
                    between {data.graph.users} counted users
                    {data.graph.edges_to_excluded > 0 && (
                      <>
                        <br />
                        <span className="stat-second">
                          +{fmt(data.graph.edges_to_excluded)} to excluded accounts
                        </span>
                      </>
                    )}
                  </>
                } />
          <Stat label="Median friends" value={fmt(data.graph.median_friends)} sub={`mean ${data.graph.mean_friends}`} />
          <Stat label="No friends at all" value={fmt(data.graph.with_no_friends)}
                sub={pct(data.graph.with_no_friends, data.graph.users)}
                tone={data.graph.with_no_friends > 0 ? 'warn' : 'good'} />
          <Stat label="Never talked to anyone" value={fmt(data.graph.never_talked_to_anyone)}
                sub="has friends, no conversation ever"
                tone={data.graph.never_talked_to_anyone > data.graph.users / 3 ? 'bad' : 'warn'} />
        </div>
        <div className="grid">
          <Card title="Friends per user" wide>
            <BarList
              rows={data.friend_distribution
                .sort((a, b) => a.sort - b.sort)
                .map(d => ({
                  label: d.bucket, value: d.users, meta: pct(d.users, data.graph.users),
                  tone: d.sort === 0 ? 'bad' : undefined,
                }))}
              unit=" users" unitOne=" user"
            />
          </Card>
        </div>
      </Section>

      {/* ═══ Supply ═════════════════════════════════════════════════════════ */}
      <Section
        id="supply"
        title="Is anyone actually reachable?"
        blurb={
          <>
            The failure ordinary analytics cannot see. Every gate here is a good idea on its own —
            quiet hours, an availability window, not being interrupted mid-call — and stacked they
            can close the app without anything looking broken. These numbers come from the app's
            own predicates, so this page and the phone cannot disagree.
          </>
        }
      >
        {supply ? (
          <>
            <div className="stat-row">
              <Stat label="Free right now" value={fmt(supply.present_now)}
                    sub={`of ${supply.users} — ${pct(supply.present_now, supply.users)}`}
                    tone={supply.present_now === 0 ? 'bad' : supply.present_now < supply.users / 4 ? 'warn' : 'good'} />
              <Stat label="Reachable by name" value={fmt(supply.reachable_by_name_now)}
                    sub={`${pct(supply.reachable_by_name_now, supply.users)} — the window doesn't gate a deliberate call`} />
              <Stat label="Can reach nobody" value={fmt(data.graph.nobody_free_right_now)}
                    sub="users with no free friend at this moment"
                    tone={data.graph.nobody_free_right_now > supply.users / 2 ? 'bad' : 'warn'} />
              <Stat label="In a call" value={fmt(supply.in_a_room)} />
            </div>
            <div className="grid">
              <Card title="Why the rest aren't free" hint="A snapshot, right now. Which gate is doing the blocking matters: “nobody is free” and “everyone is free but it's not 6pm yet” look identical and are completely different bugs.">
                {blockedRows.length
                  ? <BarList rows={blockedRows} unit=" users" unitOne=" user" />
                  : <div className="viz-empty">Nobody is blocked right now.</div>}
              </Card>
              <Card title="Friends free, per user" hint="Right now. Everyone in the first bucket cannot use the app at this moment, whatever their retention says.">
                <BarList
                  rows={(data.reachable_distribution ?? [])
                    .sort((a, b) => a.sort - b.sort)
                    .map(d => ({ label: d.bucket, value: d.users, meta: pct(d.users, supply.users), tone: d.sort === 0 ? 'bad' : undefined }))}
                  unit=" users" unitOne=" user"
                />
              </Card>
            </div>
            <div className="grid">
              <Card title="Clock coverage" hint="Presses against how many people's availability window is open at that hour, each in their own local time. A press at an hour when the windows are shut could never have worked — and that is fixed with a default, not a feature." wide>
                <DualSeries
                  points={(data.hour_of_day ?? []).map(h => ({
                    label: `${String(h.hour).padStart(2, '0')}:00`,
                    a: h.windows_open,
                    b: h.presses,
                    note: `${h.conversations} conversations`,
                  }))}
                  names={['Windows open', 'Presses (all time)']}
                />
              </Card>
            </div>
          </>
        ) : (
          <div className="viz-empty">Live reachability is unavailable — see the banner above.</div>
        )}
      </Section>

      {/* ═══ Asks ═══════════════════════════════════════════════════════════ */}
      <Section
        id="asks"
        title="What the app sends people"
        blurb={
          <>
            Counted so that growth here reads as a cost. Two earlier versions of this app died
            because they asked people for things and people don't act on asks; the current design
            deliberately replaced most pushes with quiet surfaces that request nothing. A rising
            <em> asks per active user</em> is the mistake coming back.
          </>
        }
      >
        {data.push_volume?.length ? (
          <>
            <div className="stat-row">
              {(() => {
                const recent = data.push_volume.slice(-7)
                const sum = (k: 'sent' | 'rings' | 'asks' | 'surfaces') =>
                  recent.reduce((a, r) => a + r[k], 0)
                // Active-user-days, not users: dividing a week of sends by a
                // single day's active count would overstate it sevenfold.
                const userDays = recent.reduce((a, r) => a + r.active_users, 0)
                const perAll = userDays > 0 ? sum('sent') / userDays : 0
                // The threshold is on INTERRUPTIONS only. A Live Activity
                // refreshing quietly is not the thing that killed V3 and V4;
                // being asked for something is, and folding the two together
                // would let a real ask problem hide inside surface traffic.
                const perInterrupt = userDays > 0 ? (sum('rings') + sum('asks')) / userDays : 0
                return (
                  <>
                    <Stat label="Sent, last 7 days" value={fmt(sum('sent'))}
                          sub={`${fmt(perAll, 1)} per active user per day, all kinds`} />
                    <Stat label="Interruptions per user per day" value={fmt(perInterrupt, 1)}
                          sub="rings and asks only"
                          tone={perInterrupt > 5 ? 'bad' : perInterrupt > 2 ? 'warn' : 'good'} />
                    <Stat label="Rings" value={fmt(sum('rings'))} sub="a phone actually ringing" />
                    <Stat label="Asks" value={fmt(sum('asks'))} sub="requests a yes" />
                    <Stat label="Surfaces" value={fmt(sum('surfaces'))} sub="arrives, requests nothing" />
                  </>
                )
              })()}
            </div>
            <div className="grid">
              <Card title="Sent a day" wide>
                <Columns
                  points={fillDays(data.push_volume, 30, (r, day) => ({
                    label: shortDay(day),
                    value: r?.sent ?? 0,
                    note: r ? `${r.rings} rings · ${r.asks} asks · ${r.surfaces} surfaces · ${r.active_users} actives` : undefined,
                  }))}
                />
              </Card>
              <Card title="By type, last 30 days">
                <BarList
                  rows={(data.push_by_type ?? []).map(t => ({
                    label: t.type, value: t.sent, meta: `last ${shortDay(t.last_sent)}`,
                  }))}
                />
              </Card>
            </div>
          </>
        ) : (
          <div className="viz-empty">
            Notification history starts when the nightly rollup first runs — `notifications` is
            pruned at seven days, so there is nothing older to recover.
          </div>
        )}
      </Section>

      {/* ═══ Delivery ═══════════════════════════════════════════════════════ */}
      <Section
        id="delivery"
        title="Can these phones be reached?"
        blurb={
          <>
            Not a curiosity panel. The server's ring gate parses the client version and refuses
            anything below 2.2.0, and an iPhone with no VoIP token cannot be rung at all — so an
            install base stuck on an old build is a silent outage that no other number here shows.
          </>
        }
      >
        {data.delivery ? (
          <>
            <div className="stat-row">
              <Stat label="No push token" value={fmt(data.delivery.missing_fcm)}
                    sub={`of ${data.delivery.users} — unreachable by any push`}
                    tone={data.delivery.missing_fcm > 0 ? 'warn' : 'good'} />
              <Stat label="iOS without VoIP token" value={fmt(data.delivery.ios_missing_voip)}
                    sub="cannot be rung"
                    tone={data.delivery.ios_missing_voip > data.delivery.users / 3 ? 'bad' : 'warn'} />
              <Stat label="iOS without Live Activity" value={fmt(data.delivery.ios_missing_live_activity)}
                    sub="falls back to a plain alert" />
              <Stat label="Unknown platform" value={fmt(data.delivery.unknown_platform)}
                    sub="never reported one" />
            </div>
            <div className="grid">
              <Card title="Client versions" wide>
                <table className="table">
                  <thead>
                    <tr><th>Version</th><th>Platform</th><th>Users</th><th>Active 30d</th><th>Ringable</th></tr>
                  </thead>
                  <tbody>
                    {(data.versions ?? []).map(v => (
                      <tr key={`${v.version}-${v.platform}`} className={v.active_30d > 0 && !v.ringable ? 'row-bad' : undefined}>
                        <th scope="row">{v.version}</th>
                        <td>{v.platform}</td>
                        <td>{fmt(v.users)}</td>
                        <td>{fmt(v.active_30d)}</td>
                        <td>{v.ringable ? <span className="tone-good">yes</span> : <span className="tone-bad">no</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </>
        ) : (
          <div className="viz-empty">Delivery health is unavailable — see the banner above.</div>
        )}
      </Section>

      {/* ═══ Notes ══════════════════════════════════════════════════════════ */}
      <Section
        id="notes"
        title="Notes"
        blurb="The three-day message thread. Counted from a nightly rollup, because the notes themselves are swept."
      >
        <div className="grid">
          <Card title="Notes a day" wide>
            {data.notes.length ? (
              <Columns
                points={fillDays(data.notes, 60, (r, day) => ({
                  label: shortDay(day),
                  value: r?.notes ?? 0,
                  note: r ? `${r.senders} senders · ${r.read} read` : undefined,
                }))}
              />
            ) : (
              <div className="viz-empty">No notes sent yet.</div>
            )}
          </Card>
        </div>
      </Section>

      <footer className="dash-foot">
        <p>
          Every number excludes developer accounts and rows that never got a name. Conversation and
          answer data begins {shortDay(captureDay)}; app opens before that are reconstructed from the
          auth audit log, and friendship dates before June 2026 are a migration artefact rather than
          a history. Nothing on this page is cached, by the browser or anywhere else.
        </p>
      </footer>
    </div>
  )
}
